import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, CircularProgress, Button, Card, CardContent, CardActions } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

interface SceneDetails {
  startTime: number;
  endTime: number;
}

export const ScenePage: React.FC = () => {
  const { sceneId } = useParams<{ sceneId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sceneDetails, setSceneDetails] = useState<SceneDetails | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    // Fetch scene details
    fetch(`${import.meta.env.VITE_API_URL}/api/scene-detection/scene/${sceneId}`)
      .then(response => {
        if (!response.ok) {
          throw new Error('Scene not found');
        }
        return response.json();
      })
      .then(data => {
        setSceneDetails(data);
        setIsLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setIsLoading(false);
      });
  }, [sceneId]);

  const handleDownload = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/scene-detection/scene/${sceneId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `scene_${sceneId}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading scene:', error);
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
    const videoElement = document.querySelector('video');
    if (videoElement) {
      videoElement.play();
    }
  };

  const handleBack = () => {
    navigate('/scene-detection');
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h5" color="error" gutterBottom>
          Error: {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      <Navbar />
      <Box sx={{ flex: 1 }}>
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mb: 3 }}
          >
            Back to Scene Detection
          </Button>
          
          <Card elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ position: 'relative', bgcolor: 'black', aspectRatio: '16/9' }}>
              <video
                src={`${import.meta.env.VITE_API_URL}/api/scene-detection/scene/${sceneId}`}
                controls={isPlaying}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
                onEnded={() => setIsPlaying(false)}
              >
                Your browser does not support the video tag.
              </video>
              
              {!isPlaying && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0, 0, 0, 0.4)',
                    cursor: 'pointer'
                  }}
                  onClick={handlePlay}
                >
                  <PlayArrowIcon sx={{ fontSize: 64, color: 'white' }} />
                </Box>
              )}
            </Box>
            
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Scene Details
              </Typography>
              {sceneDetails && (
                <>
                  <Typography variant="body1" color="text.secondary">
                    Duration: {Math.round((sceneDetails.endTime - sceneDetails.startTime) * 10) / 10}s
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Start Time: {Math.round(sceneDetails.startTime * 10) / 10}s
                  </Typography>
                </>
              )}
            </CardContent>
            
            <CardActions sx={{ p: 2 }}>
              <Button
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={handlePlay}
                disabled={isPlaying}
              >
                Play
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
              >
                Download
              </Button>
            </CardActions>
          </Card>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
}; 